import { RecordingWidgetState, useAssetLibraryProvider } from "@/state/providers/AssetLibraryProvider";
import classNames from "classnames";
import WidgetDocumentSelect from "./WidgetDocumentSelect";
import { useAppSelector } from "@/common/hooks/useRedux";
import WidgetMicBar from "@/features/recording-widget/WidgetMicBar";
import { useTour } from "@providers/TourProvider";

// internal icons
import FilePlusIcon from "@icons/file-plus-02.svg?react";
import MicrophoneIcon from "@icons/microphone-02.svg?react";
import Square from "@icons/square.svg?react";
import PlusCircleIcon from "@icons/plus-circle.svg?react";
import SaveIcon from "@icons/save-01.svg?react";
import SoundWaves1 from "@icons/recording-01.svg?react";
import SoundWaves2 from "@icons/recording-02.svg?react";
import { DoctorDocumentTypeContext, RecordingState } from "@/common/utils/types";
import Spinner from "@/common/components/Spinner";
import { startSentryReplay } from "@/common/utils/helpers";
import { getPatientData, getPatientVisits } from "../patient-view/state/redux/activePatientDataSlice";
import { isRecordingError } from "@/common/hooks/useAudioRecorder";
import { excludeMonitoringId } from "@/common/utils/monitoring";
import { MergedAwayRecordingNotice } from "@/features/merge-visits/MergedAwayRecordingNotice";
import { useKeepEdits } from "@/features/patient-view/components/edits/KeepEditsProvider";
import { petNamesTranscribeGenerates } from "@/features/patient-view/components/edits/choicesForPatient";

interface Props {
    pulseScale: number;
    audioTime: string;
}

// view for when widget is in the "recording" state (during recording)
const RecordingView: React.FC<Props> = ({ pulseScale, audioTime }) => {
    const {
        // audio recording controls
        recorderControls: { togglePauseResume, recordingState, pauseResumeSupported, isExtensionTransport },
        // recording widget controls
        recordingWidgetControls: {
            recordingWidgetState,
            setRecordingWidgetState,
            handleFinishRecording,
            screenMayTurnOff,
        },
        // patient data controls
        recordingPatientControls: { patientName, patientRecordId, visitPatientName },
        // document select controls
        recordingDocumentSelectControls: { setGenerateOnStop },
    } = useAssetLibraryProvider();

    const { activeTour } = useTour();
    const { selectedDocuments: selectedVisitDocuments, selectedHistoryDocuments } = useAppSelector(
        (state) => state.documents,
    );
    const allVisits = useAppSelector(getPatientVisits);
    const patientData = useAppSelector(getPatientData);
    const { askAtStop } = useKeepEdits();

    const isPaused = recordingWidgetState === RecordingWidgetState.PAUSED;
    const isLoading =
        recordingWidgetState === RecordingWidgetState.LOADING || recordingState === RecordingState.loading;
    const isRecordingStateError = isRecordingError(recordingState);

    // Determine if we're in a history visit
    const isHistory = allVisits.find((visit) => visit.id === patientData?.id)?.isHistory;
    const context = isHistory ? DoctorDocumentTypeContext.History : DoctorDocumentTypeContext.Default;
    // Monitoring is excluded here: it can be present in this shared selection state (selected via
    // the main sidebar's DocumentSelect), but generate-on-stop can never act on it — transcribeAudio
    // strips it before sending. Counting it toward "something is selected" would arm Stop & Generate
    // on a selection that silently produces no Monitoring output.
    const selectedDocumentIdsForContext = excludeMonitoringId(
        context === DoctorDocumentTypeContext.History ? selectedHistoryDocuments : selectedVisitDocuments,
    );

    /**
     * Handles the toggle action for pausing and resuming the recording.
     *
     * @param {React.MouseEvent<HTMLButtonElement>} e - The mouse event triggered by clicking the button.
     * @returns {void}
     */
    const handleTogglePauseResume = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.preventDefault();
        startSentryReplay();
        togglePauseResume();
    };

    /**
     * Save and Close
     * handler for the save and close button in the recording view
     */
    const handleSaveAndClose = async (e: React.MouseEvent<HTMLButtonElement>) => {
        // lock mouse events
        e.preventDefault();

        // start sentry replay
        startSentryReplay();

        // set widget state back to uploading
        setRecordingWidgetState(RecordingWidgetState.UPLOADING);

        // set generate on stop to false
        setGenerateOnStop(false);

        // save recording
        await handleFinishRecording();
    };

    /**
     * Generate Documents
     * handler for the generate button in the recording view
     */
    const handleGenerate = async (e: React.MouseEvent<HTMLButtonElement>) => {
        // lock mouse events
        e.preventDefault();

        // start sentry replay
        startSentryReplay();

        // set widget state back to uploading
        setRecordingWidgetState(RecordingWidgetState.UPLOADING);

        // set generate on stop to true
        setGenerateOnStop(true);

        // EN-1492 (D18-D20): ask about edits on the selected types while the recording uploads. Called before the
        // upload so its pending register goes out first, but not awaited: the upload and the Transcribe start never
        // wait on the check or the dialog. The pets are every pet Transcribe generates for, from the recording
        // visit's own name (the open visit's current one when it is the same visit, which follows a rename), not the
        // open visit's selection.
        if (patientRecordId) {
            const visitName =
                patientRecordId === patientData?.id
                    ? patientData.patient_name || patientData.patients?.name || visitPatientName
                    : visitPatientName;
            const pets = petNamesTranscribeGenerates(visitName);
            void askAtStop({
                patientRecordId,
                documentTypeIds: selectedDocumentIdsForContext,
                patientNames: pets.length > 0 ? pets : undefined,
            });
        }

        // save recording
        await handleFinishRecording();
    };

    /**
     * A function to get the proper dialog to display depending on what state the widget is in
     * and if the patient name is defined
     *
     * @param isPaused: a boolean indicating whether the recording widget is in the paused state
     * @param patientName: the patient name provided in the widget when recording started
     */
    const getDialog = (isPaused: boolean, isRecordingStateError: boolean, patientName: string | undefined) => {
        // check if the widget is in the paused state
        if (isRecordingStateError) {
            return <span className="text-white">Recording stopped</span>;
        } else if (isPaused) {
            // if paused and patient name, display the patient name
            if (patientName) {
                return (
                    <>
                        <span className="text-white">Continue recording or generate selected documents for</span>
                        <span className="ml-1 text-max-600">{patientName}</span>
                    </>
                );
            } else {
                // otherwise, display paused dialog as is
                return (
                    <>
                        <span className="text-white">Continue recording or generate selected documents</span>
                    </>
                );
            }
        } else {
            // if not paused and patient name, display the patient name with recording dialog
            if (patientName) {
                return (
                    <>
                        <span className="text-white">
                            Recording a Clip for
                            <br />
                        </span>
                        <span className="text-max-600">{patientName}</span>
                    </>
                );
            } else {
                // otherwise, display recording dialog as is
                return (
                    <>
                        <span className="text-white">Recording a Clip</span>
                    </>
                );
            }
        }
    };

    return (
        <div className="w-full h-full">
            <div className="w-full h-[129px] flex">
                {/* left column: microphone button */}
                <div className="w-[100px] flex items-center justify-center ml-4">
                    {isLoading ? (
                        <div className="relative w-[60px] h-[60px] rounded-full flex items-center justify-center ">
                            <Spinner color="text-white" />
                        </div>
                    ) : (
                        <button
                            id={isPaused ? "recording-widget-pause-button" : "recording-widget-stop-button"}
                            data-testid="stop-recording"
                            type="button"
                            disabled={!pauseResumeSupported && !isRecordingStateError}
                            className={classNames(
                                "relative w-[60px] h-[60px] rounded-full flex items-center justify-center text-xs font-semibold shadow-sm transition-transform",
                                !pauseResumeSupported && !isRecordingStateError
                                    ? "opacity-40 cursor-not-allowed bg-red-500 text-white"
                                    : isPaused || isRecordingStateError
                                      ? "bg-max-900 hover:bg-max-800 text-white group-hover:bg-max-800 group cursor-pointer"
                                      : "bg-red-500 text-white hover:bg-red-600 cursor-pointer",
                            )}
                            style={{
                                boxShadow: isPaused
                                    ? "0 0 0 12px rgba(112, 128, 154, 0.50)"
                                    : `0 0 0 ${8 + (pulseScale > 0.25 ? 0.25 : pulseScale) * 6 * 8}px rgba(112, 128, 154, 0.50)`,
                            }}
                            onClick={(e) => handleTogglePauseResume(e)}
                        >
                            {isPaused || isRecordingStateError ? (
                                <>
                                    <div>
                                        <MicrophoneIcon className="h-10 w-10 text-white" />
                                    </div>
                                    <div className="w-6 h-6 absolute top-0 right-0 text-white bg-max-900 rounded-full justify-center items-center inline-flex group-hover:bg-max-800">
                                        <PlusCircleIcon className="w-4 h-4" />
                                    </div>
                                </>
                            ) : (
                                <Square className="w-10 h-10 text-white" />
                            )}
                        </button>
                    )}
                </div>

                {/* right column: text and sound wave icons */}
                <div className="flex flex-col justify-between w-full px-6 my-4">
                    {/* dialog */}
                    <div className="text-sm font-medium leading-tight h-[60px]">
                        {getDialog(isPaused, isRecordingStateError, patientName)}
                    </div>

                    {/* sound wave icons */}
                    <div className="mt-2 flex flex-col items-start">
                        <div className="bg-shadow-800 w-[144px] rounded-lg">
                            <div className="px-4 py-2 flex items-center text-white">
                                <div className="flex flex-row">
                                    <SoundWaves1 className="w-6 h-6" />
                                    <SoundWaves2 className="w-6 h-6" />
                                    <SoundWaves1 className="w-6 h-6" />
                                </div>
                                <div
                                    data-testid="recording-timer"
                                    className="pl-2 text-center text-sm font-medium leading-tight"
                                >
                                    {audioTime}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <MergedAwayRecordingNotice />
            {/* EN-1383: own state, not recordingMessage, which other warnings replace at any time */}
            {screenMayTurnOff && !activeTour && (
                <div
                    data-testid="screen-may-turn-off-notice"
                    className="flex items-center justify-center py-2 px-2 text-xs text-start font-medium bg-[#F79009] text-[#FEF0C7]"
                >
                    <span>
                        Your screen may turn off on its own. Keep it on, or parts of this visit may not be recorded.
                    </span>
                </div>
            )}
            <div className="w-full justify-left">
                <WidgetDocumentSelect />
                <div>
                    <div
                        data-tour="recording-widget-actions"
                        className={classNames(
                            "w-full h-[76px] bg-shadow-800 flex flex-row items-center",
                            isExtensionTransport && "rounded-b-lg",
                        )}
                    >
                        <button
                            id="recording-widget-save"
                            data-testid="stop-and-close"
                            className="w-full h-9 ml-6 mr-2.5 bg-white border-max-300 text-max-800 rounded-lg shadow border justify-center items-center inline-flex text-xs sm:text-sm font-semibold leading-tight"
                            onClick={handleSaveAndClose}
                        >
                            <SaveIcon className="w-4 h-4 sm:w-5 sm:h-5 relative mr-1" />
                            {isPaused ? <>Save & Close</> : <>Stop & Close</>}
                        </button>
                        <button
                            data-tour="recording-widget-generate"
                            id="recording-widget-generate"
                            data-testid="stop-and-generate"
                            disabled={selectedDocumentIdsForContext.length === 0}
                            className={classNames(
                                "w-full h-9 ml-2.5 mr-6 rounded-lg shadow border justify-center items-center inline-flex text-xs sm:text-sm font-semibold leading-tight",
                                selectedDocumentIdsForContext.length === 0
                                    ? "border-shadow-300 text-shadow-300 bg-shadow-700"
                                    : "border-max-900 text-white bg-max-900",
                            )}
                            onClick={handleGenerate}
                        >
                            <FilePlusIcon className="w-4 h-4 sm:w-4 sm:h-4 relative mr-1" />
                            {isPaused ? <>Generate</> : <>Stop & Generate</>}
                        </button>
                    </div>
                </div>
            </div>
            <WidgetMicBar />
        </div>
    );
};

export default RecordingView;
